/* eslint-disable quotes */
import Pages from "enums/Pages";
import { Sliders, Download } from "react-feather";
import LogReportFilter, {
  FormFilterLogs
} from "pages/LogReport/LogReportFilter";
import { format, parse, set } from "date-fns";
import { useIsMount } from "hooks/useIsMount";
import { useTranslation } from "react-i18next";
import { useAuth } from "contexts/AuthContext";
import InnerPageLayout from "layouts/InnerPageLayout";
import { useLocale } from "contexts/LocaleContext";
import DataTable from "components/DataTable";
import DefaultPageLayout from "layouts/DefaultPageLayout";
import useLogReportAPI, { LogReport } from "api/LogReportAPI";
import { FC, useCallback, useEffect, useState } from "react";
import { usePageLocation } from "contexts/PageLocationContext";
import { useErrorHandler } from "contexts/ErrorHandlerContext";
import { dateTimeToString } from "utils/DateFunctions";
import PageSection from "components/PageSection/PageSection";
import PageSectionHeaderAction from "components/PageSection/PageSectionHeaderAction";
import {
  PaginatorModel,
  defaultPaginatorModelValues
} from "components/Paginator";
const PUMATRONIX_CLIENTE = "Cliente";
const BATCH_SIZE = 1000;

const LogReportPage: FC = () => {
  const LogReportAPI = useLogReportAPI();
  const isMount = useIsMount();
  const { sessionUser } = useAuth();
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [loadedData, setLoadedData] = useState<LogReport[]>([]);
  const [loadedPageRange, setLoadedPageRange] = useState<{
    start: number;
    end: number;
  }>({ start: 0, end: 0 });
  const [page, setPage] = useState(1);
  const [paginator, setPaginator] = useState<PaginatorModel>(
    defaultPaginatorModelValues
  );
  const [pageSize, setPageSize] = useState<number>(10);
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const { language } = useLocale();
  const defaultFilterValues: FormFilterLogs = {
    user: "",
    message: "",
    startDate: set(new Date(), { hours: 0, minutes: 0, seconds: 0 }),
    endDate: new Date(),
    startTime: set(new Date(), { hours: 0, minutes: 0, seconds: 0 }),
    endTime: new Date()
  };
  const [reportResults, setReportResults] = useState<LogReport[]>([]);
  const [filterData, setFilterData] =
    useState<FormFilterLogs>(defaultFilterValues);
  const { t } = useTranslation();
  const { errorHandler } = useErrorHandler();

  const handleExportPDF = async () => {
    if (!sessionUser?.["customer_id"] || !filterData) return;
    setIsExporting(true);
    try {
      const pdfResponse = await LogReportAPI.downloadPdf({
        customer_id: sessionUser["customer_id"],
        user: sessionUser["username"],
        message: filterData.message,
        final_date: dateTimeToString(
          filterData.endDate as Date,
          filterData.endTime as Date
        ),
        initial_date: dateTimeToString(
          filterData.startDate as Date,
          filterData.startTime as Date
        ),
        language: language.split("-")[0]
      });

      const linkSource = `data:application/pdf;base64,${pdfResponse}`;
      const downloadLink = document.createElement("a");
      const date = format(new Date(), "yyyy-MM-dd");
      const fileName = `log_report_${date}.pdf`;

      downloadLink.href = linkSource;
      downloadLink.download = fileName;
      downloadLink.click();
    } catch (error) {
      errorHandler({ error });
    } finally {
      setIsExporting(false);
    }
  };

  const requestData = useCallback(
    async (
      filter: FormFilterLogs | undefined,
      pageValue: number,
      pageSizeValue: number
    ) => {
      if (!sessionUser?.["customer_id"] || !filter) return;
      const startItem = (pageValue - 1) * pageSizeValue;
      const endItem = startItem + pageSizeValue;
      const loadedStartItem = (loadedPageRange.start - 1) * pageSizeValue;
      const loadedEndItem = loadedPageRange.end * pageSizeValue;

      const needsNewData =
        pageValue === 1 ||
        loadedData.length === 0 ||
        startItem < loadedStartItem ||
        endItem > loadedEndItem;

      if (needsNewData) {
        setIsLoading(true);
        const batchPage = Math.floor(startItem / BATCH_SIZE) + 1;

        try {
          const reportResponse = await LogReportAPI.list({
            ["customer_id"]: sessionUser["customer_id"],
            page: batchPage,
            user:
              sessionUser?.level_name === PUMATRONIX_CLIENTE
                ? sessionUser?.username
                : filter.user,
            message: filter.message,
            ["final_date"]: dateTimeToString(
              filter.endDate as Date,
              filter.endTime as Date
            ),
            ["initial_date"]: dateTimeToString(
              filter.startDate as Date,
              filter.startTime as Date
            ),
            ["page_size"]: BATCH_SIZE
          });

          const newBatchData = reportResponse.logs || [];

          const newBatchStartPage =
            (batchPage - 1) * Math.floor(BATCH_SIZE / pageSizeValue) + 1;
          const newBatchEndPage = Math.min(
            newBatchStartPage + Math.floor(BATCH_SIZE / pageSizeValue) - 1,
            Math.ceil((reportResponse.total_count || 0) / pageSizeValue)
          );

          const isDistantJump =
            loadedData.length > 0 &&
            (newBatchStartPage > loadedPageRange.end + 1 ||
              newBatchEndPage < loadedPageRange.start - 1);

          let mergedData: LogReport[];
          let mergedPageRange: { start: number; end: number };

          if (loadedData.length === 0 || isDistantJump) {
            mergedData = newBatchData;
            mergedPageRange = {
              start: newBatchStartPage,
              end: newBatchEndPage
            };
          } else if (newBatchStartPage < loadedPageRange.start) {
            mergedData = [...newBatchData, ...loadedData];
            mergedPageRange = {
              start: newBatchStartPage,
              end: loadedPageRange.end
            };
          } else {
            mergedData = [...loadedData, ...newBatchData];
            mergedPageRange = {
              start: loadedPageRange.start,
              end: newBatchEndPage
            };
          }

          setLoadedData(mergedData);
          setLoadedPageRange(mergedPageRange);
          setPaginator({
            totalPages: Math.ceil(
              (reportResponse.total_count || 0) / pageSizeValue
            ),
            totalItems: reportResponse.total_count || 0
          });

          const pageStartIndex =
            startItem - (mergedPageRange.start - 1) * pageSizeValue;
          const pageEndIndex =
            endItem - (mergedPageRange.start - 1) * pageSizeValue;
          const currentPageData = mergedData.slice(
            pageStartIndex,
            pageEndIndex
          );

          setReportResults(currentPageData);
          setPage(pageValue);
          setIsLoading(false);
        } catch (error) {
          errorHandler({ error });
          setIsLoading(false);
        }
      } else {
        const pageStartIndex = startItem - loadedStartItem;
        const pageEndIndex = endItem - loadedStartItem;
        const currentPageData = loadedData.slice(pageStartIndex, pageEndIndex);

        setReportResults(currentPageData);
        setPage(pageValue);
      }
    },
    [sessionUser, loadedData, loadedPageRange, LogReportAPI, errorHandler]
  );

  useEffect(() => {
    setLoadedData([]);
    setLoadedPageRange({ start: 0, end: 0 });
    requestData(filterData, 1, pageSize);
  }, [pageSize]);

  useEffect(() => {
    if (!isMount) {
      if (filterData === null) {
        requestData(filterData, page, pageSize);
      } else {
        requestData(filterData, 1, pageSize);
      }
    }
  }, [filterData]);

  const onFilter = (data: FormFilterLogs) => {
    setFilterData(data);
  };

  const { setPageTitle, setLocation } = usePageLocation();

  useEffect(() => {
    setPageTitle(t("windowTitle.logReport"));
    setLocation([
      {
        label: t("menu.reports")
      },
      {
        label: t("LogReportPage.title"),
        page: Pages.LOG_REPORT
      }
    ]);
  }, [t, Pages]);

  const handleExportCSV = async () => {
    if (!sessionUser?.["customer_id"] || !filterData) return;

    setIsExporting(true);
    try {
      const exportResponse = await LogReportAPI.list({
        ["customer_id"]: sessionUser["customer_id"],
        page: 1,
        user:
          sessionUser?.level_name === PUMATRONIX_CLIENTE
            ? sessionUser?.username
            : filterData.user,
        message: filterData.message,
        ["final_date"]: dateTimeToString(
          filterData.endDate as Date,
          filterData.endTime as Date
        ),
        ["initial_date"]: dateTimeToString(
          filterData.startDate as Date,
          filterData.startTime as Date
        ),
        ["page_size"]: 10000
      });

      const exportData = exportResponse.logs || [];

      const headers = [
        { key: "date_time", label: t("LogReportPage.datetime") },
        { key: "user", label: t("LogReportPage.userType") },
        { key: "message", label: t("LogReportPage.message") }
      ];

      const headerRow = headers.map(header => `"${header.label}"`).join(",");

      const dataRows = exportData.map(row => {
        const formattedDateTime = format(
          parse(row.date_time, "dd/MM/yyyy HH:mm:ss.SSS", new Date()),
          t("LogReportPage.dateTimeFormat")
        );

        return [
          `"${formattedDateTime.replace(/"/g, '""')}"`,
          `"${row.user.replace(/"/g, '""')}"`,
          `"${row.message.replace(/"/g, '""')}"`
        ].join(",");
      });

      const csvContent = [headerRow, ...dataRows].join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      const date = format(new Date(), "yyyy-MM-dd");
      const filename = `log_report_${date}.csv`;
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      errorHandler({ error });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <DefaultPageLayout>
      <InnerPageLayout>
        <PageSection
          title={t("LogReportPage.title")}
          isLoading={isLoading}
          primaryActions={
            <>
              <PageSectionHeaderAction
                variant="contained"
                color="primary"
                startIcon={<Download />}
                onClick={handleExportPDF}
                disabled={
                  isLoading || isExporting || reportResults.length === 0
                }
              >
                {isExporting
                  ? t("waitState.loading")
                  : t("LogReportPage.downloadPdf")}
              </PageSectionHeaderAction>
              <PageSectionHeaderAction
                variant="contained"
                color="primary"
                startIcon={<Download />}
                onClick={handleExportCSV}
                disabled={
                  isLoading || isExporting || reportResults.length === 0
                }
              >
                {isExporting
                  ? t("waitState.loading")
                  : t("LogReportPage.downloadCsv")}
              </PageSectionHeaderAction>
              <PageSectionHeaderAction
                variant="contained"
                color="secondary"
                startIcon={<Sliders />}
                onClick={() => setIsFilterOpen(true)}
              >
                {t("action.filter")}
              </PageSectionHeaderAction>
            </>
          }
        >
          <DataTable
            watermarked
            headers={[
              { key: "date_time", label: t("LogReportPage.datetime") },
              { key: "user", label: t("LogReportPage.userType") },
              { key: "message", label: t("LogReportPage.message") }
            ]}
            defaultSort={["date_time", "desc"]}
            onHeaderSort={setReportResults}
            data={reportResults}
            renderRow={row => [
              <>
                {format(
                  parse(row.date_time, "dd/MM/yyyy HH:mm:ss.SSS", new Date()),
                  t("LogReportPage.dateTimeFormat")
                )}
              </>,
              <>{row.user}</>,
              <>{row.message}</>
            ]}
            hideColumnsXs={[2]}
            page={page}
            onPageChange={pageValue =>
              requestData(filterData, pageValue, pageSize)
            }
            pageSize={pageSize}
            onPageSizeChange={setPageSize}
            totalPages={paginator.totalPages}
            totalItems={paginator.totalItems}
            isLoading={isLoading}
          />
        </PageSection>
      </InnerPageLayout>
      <LogReportFilter
        open={isFilterOpen}
        setOpen={setIsFilterOpen}
        onFilter={data => onFilter(data)}
      />
    </DefaultPageLayout>
  );
};

export default LogReportPage;
